import { type Floor, type Gateway, type Mesh } from "@eisox/api/core-api";
import dayjs, { type ConfigType } from "@eisox/dayjs";
import { BORDER_ROUTER_STATUS, GATEWAY_ERRORS_ENUM, GATEWAY_WARNINGS_ENUM, getGatewayProblem } from "@eisox/gateways";

import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedGateway } from "~/types/gateway.type";

type StatusHandlers<T extends string> = Record<T, (g: EnhancedGateway, showDate: boolean) => string>;

export const GATEWAYS_ERRORS_TRANSLATION: StatusHandlers<GATEWAY_ERRORS_ENUM> = {
  [GATEWAY_ERRORS_ENUM.DISCONNECTED]: (g, showDate) =>
    showDate
      ? m.offline_since({ date: g.lastGatewayActivity ? dayjs(g.lastGatewayActivity).format("L LT") : "-" })
      : m.offline(),
  [GATEWAY_ERRORS_ENUM.BORDER_ROUTER_STATUS]: () => m.border_router_problem(),
};

export const GATEWAYS_WARNINGS_TRANSLATION: StatusHandlers<GATEWAY_WARNINGS_ENUM> = {
  [GATEWAY_WARNINGS_ENUM.NOT_INSTALL]: () => m.in_pre_installation(),
  [GATEWAY_WARNINGS_ENUM.DISCONNECTED_WARNING]: (g, showDate) =>
    m.gateway_unavailable({
      since: showDate ? "true" : "false",
      date: g.lastGatewayActivity ? dayjs(g.lastGatewayActivity).format("L LT") : "-",
    }),
  [GATEWAY_WARNINGS_ENUM.NOT_ACTIVATED]: () => m.not_activated(),
};

export const getGatewayProblemTranslation = (gateway: EnhancedGateway) => {
  const errors = gateway.errors.map(error => GATEWAYS_ERRORS_TRANSLATION[error](gateway, true));
  const warnings = gateway.warnings.map(warning => GATEWAYS_WARNINGS_TRANSLATION[warning](gateway, true));
  return { errors, warnings };
};

export const GATEWAY_ONLY_INSTALLER_WARNINGS = [GATEWAY_WARNINGS_ENUM.DISCONNECTED_WARNING];

export const gatewaysToEnhancedGateways = (
  gateways: Gateway[],
  floors: Floor[],
  meshes: Mesh[],
  date: ConfigType,
  permissions: Permission,
  canSeeInstallerWarnings: boolean,
): EnhancedGateway[] =>
  gateways.map(g => {
    const floor = floors.find(f => f.id === g.floor.id);

    if (!floor) throw new Error(`Floor ${g.floor.id} not found for gateway ${g.id}`);

    const problem = getGatewayProblem(
      { ...g, borderRouterStatus: [g.borderRouterStatus as unknown as BORDER_ROUTER_STATUS] },
      date,
    );
    const errors = permissions.gateway?.error ? problem.errors : [];
    const warnings = permissions.gateway?.warning
      ? canSeeInstallerWarnings
        ? problem.warnings
        : problem.warnings.filter(w => !GATEWAY_ONLY_INSTALLER_WARNINGS.includes(w as GATEWAY_WARNINGS_ENUM))
      : [];
    const problemStatus =
      (problem.problemStatus === "error" && permissions.gateway?.error) ||
      (problem.problemStatus === "warning" && warnings.length > 0 && permissions.gateway?.warning)
        ? problem.problemStatus
        : undefined;

    return {
      ...g,
      problemStatus,
      errors,
      warnings,
      floor: { x: g.floor.x, y: g.floor.y, id: floor.id, name: floor.name },
      meshes:
        g.meshes?.map(m => {
          const mesh = meshes.find(mesh => mesh.id === m.id);
          return { ...m, id: m.id, name: mesh?.name ?? "-" };
        }) ?? [],
    };
  });
